<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { useCardBookmark } from "@/composables/useCardBookmark";
import { useCardTelemetry } from "@/composables/useCardTelemetry";
import isUnauth from "@/utils/isUnauth";

const props = withDefaults(
  defineProps<{
    result: any;
    //eslint-disable-next-line vue/no-unused-properties
    index?: number;
    status?: any;
    forCarousel?: boolean;
    cardView?: string;
    cardStyle?: string;
    priceView?: string;
    topicView?: boolean;
    isStoreContent?: boolean;
    showDescription?: boolean;
  }>(),
  {
    index: 0,
    status: null,
    forCarousel: false,
    cardView: "Grid",
    cardStyle: "Grid",
    priceView: "USD",
    topicView: false,
    isStoreContent: false,
    showDescription: true,
  }
);

const router = useRouter();

// Composables
const {
  bookmarkMe,
  debouncedBookmarkMe,
  addContentToMyList,
  removeContentFromMyList,
} = useCardBookmark();

const { sendTelemetry: sendTelemetryBase } = useCardTelemetry();

// Computed properties
const resultLink = computed(() => {
  let contentBundleId;
  if (props.result?.friendly_url?.length > 0) {
    contentBundleId = props.result.friendly_url;
  } else {
    contentBundleId = props.result.id;
  }

  if (!contentBundleId) {
    return "";
  }
  return router.resolve({
    name: "content-bundle",
    params: { contentBundleId },
  }).href;
});

// Methods
const sendTelemetry = async () => {
  await sendTelemetryBase(props, resultLink.value);
};

defineExpose({
  sendTelemetry,
  bookmarkMe,
  debouncedBookmarkMe,
  addContentToMyList,
  removeContentFromMyList,
});
</script>

<template>
  <div
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
    data-cy="bundle-result"
    v-show="result"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :cy="'bundle-card'"
      :link="resultLink"
      :show-description="props.showDescription !== false"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :cy="'bundle-product-card'"
      :link="resultLink"
      :show-description="props.showDescription !== false"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
  </div>
</template>
